import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList, dvGet, dvCreate, dvUpdate } from '../api/dataverseClient';
import { getGraphHeaders, getDataverseToken } from '../auth/authService';
import { getDataverseApiBase } from './environmentService';


export interface ImportJob {
  dagrun_pm_importjobid: string;
  dagrun_pm_name: string;
  dagrun_pm_sourcetype: number;
  dagrun_pm_sourceidentifier?: string | null;
  dagrun_pm_status: number;
  dagrun_pm_rowstotal: number | null;
  dagrun_pm_rowsimported: number | null;
  dagrun_pm_rowsfailed: number | null;
  dagrun_pm_rowsduplicate: number | null;
  dagrun_pm_importsummaryjson?: string | null;
  _dagrun_pm_targetprojectid_value?: string | null;
  createdon: string;
}

export interface ImportLogEntry {
  dagrun_pm_importlogid: string;
  dagrun_pm_name: string;
  dagrun_pm_rowindex: number;
  dagrun_pm_errormessage?: string | null;
  dagrun_pm_issuetype: number;
  dagrun_pm_resolutionstatus: number;
}

export const ImportSourceType = {
  Planner: 100000000,
  ProjectOnline: 100000001,
  ProjectProfessional: 100000002,
  ExcelCSV: 100000003,
  Monday: 100000004,
  Smartsheet: 100000005,
  Jira: 100000006,
  Asana: 100000007,
  ClickUp: 100000008,
  Trello: 100000009,
} as const;

export const ImportStatus = {
  Pending: 100000000,
  InProgress: 100000001,
  Completed: 100000002,
  Failed: 100000003,
  PartialSuccess: 100000004,
} as const;

export const SOURCE_LABELS: Record<number, string> = {
  100000000: 'Planner',
  100000001: 'Project Online',
  100000002: 'Microsoft Project',
  100000003: 'Excel / CSV',
  100000004: 'Monday.com',
  100000005: 'Smartsheet',
  100000006: 'Jira',
  100000007: 'Asana',
  100000008: 'ClickUp',
  100000009: 'Trello',
};

export const STATUS_LABELS: Record<number, string> = {
  100000000: 'Pending',
  100000001: 'In Progress',
  100000002: 'Completed',
  100000003: 'Failed',
  100000004: 'Partial Success',
};

const FIELDS = [
  'dagrun_pm_importjobid','dagrun_pm_name','dagrun_pm_sourcetype','dagrun_pm_sourceidentifier',
  'dagrun_pm_status','dagrun_pm_rowstotal','dagrun_pm_rowsimported','dagrun_pm_rowsfailed',
  'dagrun_pm_rowsduplicate','dagrun_pm_importsummaryjson','_dagrun_pm_targetprojectid_value','createdon',
];

export async function getImportJobs(pca: IPublicClientApplication): Promise<ImportJob[]> {
  return dvList<ImportJob>(pca, 'dagrun_pm_importjobs', { select: FIELDS, orderby: 'createdon desc', top: 20 });
}

export async function getImportJob(pca: IPublicClientApplication, id: string): Promise<ImportJob> {
  return dvGet<ImportJob>(pca, 'dagrun_pm_importjobs', id, { select: FIELDS });
}

export async function createImportJob(pca: IPublicClientApplication, data: Record<string, unknown>): Promise<string> {
  return dvCreate(pca, 'dagrun_pm_importjobs', data);
}

export async function updateImportJob(pca: IPublicClientApplication, id: string, data: Record<string, unknown>): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_importjobs', id, data);
}

export async function getImportLog(pca: IPublicClientApplication, jobId: string): Promise<ImportLogEntry[]> {
  return dvList<ImportLogEntry>(pca, 'dagrun_pm_importlogs', {
    select: ['dagrun_pm_importlogid','dagrun_pm_name','dagrun_pm_rowindex','dagrun_pm_errormessage','dagrun_pm_issuetype','dagrun_pm_resolutionstatus'],
    filter: `_dagrun_pm_importjobid_value eq ${jobId}`,
    orderby: 'dagrun_pm_rowindex asc',
  });
}

export async function fetchPlannerPlans(pca: IPublicClientApplication): Promise<{ id: string; title: string }[]> {
  const headers = await getGraphHeaders(pca);
  const res = await fetch('https://graph.microsoft.com/v1.0/me/planner/plans', { headers });
  if (!res.ok) throw new Error(`Planner API failed: ${res.status}`);
  const data = await res.json();
  return (data.value ?? []).map((p: any) => ({ id: p.id, title: p.title }));
}

export async function fetchPlannerTasks(pca: IPublicClientApplication, planId: string): Promise<any[]> {
  const headers = await getGraphHeaders(pca);
  const res = await fetch(`https://graph.microsoft.com/v1.0/planner/plans/${planId}/tasks`, { headers });
  if (!res.ok) throw new Error(`Planner tasks API failed: ${res.status}`);
  return (await res.json()).value ?? [];
}

export async function fetchPlannerBuckets(pca: IPublicClientApplication, planId: string): Promise<{ id: string; name: string }[]> {
  const headers = await getGraphHeaders(pca);
  const res = await fetch(`https://graph.microsoft.com/v1.0/planner/plans/${planId}/buckets`, { headers });
  if (!res.ok) throw new Error(`Planner buckets API failed: ${res.status}`);
  return ((await res.json()).value ?? []).map((b: any) => ({ id: b.id, name: b.name }));
}

// ─── OData $batch bulk insert with 429 backoff ─────────────────────────
//
// Each batch is a single POST to /$batch counted as one API call against
// Dataverse service protection limits. Operations are sent as independent
// (non-changeset) parts so a failure in one row does not roll back the
// others — partial-success per batch is parsed from the multipart response.

export const IMPORT_BATCH_SIZE = 100;

// The locked 20-swatch Dagrun palette (mirrors BoardView's DAGRUN_COLORS,
// which is not exported). Imported collections cycle through these.
export const IMPORT_COLLECTION_COLORS = [
  '#0E7490', '#0A5568', '#17A2C0', '#111318', '#6D28D9', '#4C1D95', '#0D6E6E',
  '#065F5F', '#DC2626', '#991B1B', '#7C3AED', '#5B21B6', '#D97706', '#92400E',
  '#374151', '#1F2937', '#059669', '#047857', '#475569', '#334155',
];

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export interface BatchProgress {
  batchIndex: number;
  batchCount: number;
  imported: number;
  failed: number;
  total: number;
}

interface BatchCreateResult {
  ids: (string | null)[];     // created GUID per input row (null on failure), index-aligned
  errors: (string | null)[];  // error text per input row (null on success), index-aligned
}

function buildBatchBody(entitySet: string, bodies: Record<string, unknown>[], boundary: string): string {
  const parts = bodies.map((b, i) => [
    `--${boundary}`,
    'Content-Type: application/http',
    'Content-Transfer-Encoding: binary',
    `Content-ID: ${i + 1}`,
    '',
    `POST ${getDataverseApiBase()}/${entitySet} HTTP/1.1`,
    'Content-Type: application/json',
    '',
    JSON.stringify(b),
  ].join('\r\n'));
  return parts.join('\r\n') + `\r\n--${boundary}--\r\n`;
}

// Parse a multipart $batch response into one result per sub-operation, in order.
function parseBatchResponse(text: string): { status: number; entityId: string | null; error: string | null }[] {
  const matches = [...text.matchAll(/HTTP\/1\.1 (\d{3})/g)];
  return matches.map((m, i) => {
    const status = Number(m[1]);
    const start = m.index ?? 0;
    const end = i + 1 < matches.length ? (matches[i + 1].index ?? text.length) : text.length;
    const seg = text.slice(start, end);
    if (status >= 200 && status < 300) {
      const eid = seg.match(/OData-EntityId:\s*\S*\(([0-9a-f-]{36})\)/i);
      return { status, entityId: eid ? eid[1] : null, error: null };
    }
    let error = `HTTP ${status}`;
    const jsonMatch = seg.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      try {
        const j = JSON.parse(jsonMatch[0]);
        error = j?.error?.message ?? JSON.stringify(j).slice(0, 300);
      } catch { /* keep HTTP status */ }
    }
    return { status, entityId: null, error };
  });
}

// Send one $batch (<= one chunk). Handles a 429 with a single Retry-After
// wait + one retry; a still-429 result marks the whole chunk failed.
async function dataverseBatchCreate(
  pca: IPublicClientApplication, entitySet: string, bodies: Record<string, unknown>[],
): Promise<BatchCreateResult> {
  if (bodies.length === 0) return { ids: [], errors: [] };
  const token = await getDataverseToken(pca);
  const boundary = `batch_${crypto.randomUUID()}`;
  const body = buildBatchBody(entitySet, bodies, boundary);

  const doPost = () => fetch(`${getDataverseApiBase()}/$batch`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': `multipart/mixed; boundary=${boundary}`,
      Accept: 'application/json',
      'OData-MaxVersion': '4.0',
      'OData-Version': '4.0',
    },
    body,
  });

  let res = await doPost();
  if (res.status === 429) {
    const retryAfter = Number(res.headers.get('Retry-After')) || 60;
    await sleep(retryAfter * 1000);
    res = await doPost();
    if (res.status === 429) {
      const msg = 'Dataverse service protection limit (429) — batch skipped after one retry';
      return { ids: bodies.map(() => null), errors: bodies.map(() => msg) };
    }
  }
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    const msg = `Batch request failed: ${res.status} ${detail.slice(0, 200)}`;
    return { ids: bodies.map(() => null), errors: bodies.map(() => msg) };
  }

  const parsed = parseBatchResponse(await res.text());
  const ids: (string | null)[] = [];
  const errors: (string | null)[] = [];
  for (let i = 0; i < bodies.length; i++) {
    const r = parsed[i];
    if (r && r.status >= 200 && r.status < 300) { ids.push(r.entityId); errors.push(null); }
    else { ids.push(null); errors.push(r?.error ?? `No response for row ${i + 1}`); }
  }
  return { ids, errors };
}

async function createImportLogEntry(pca: IPublicClientApplication, jobId: string, rowIndex: number, message: string): Promise<void> {
  try {
    await dvCreate(pca, 'dagrun_pm_importlogs', {
      dagrun_pm_name: `Error row ${rowIndex}`.slice(0, 100),
      dagrun_pm_rowindex: rowIndex,
      dagrun_pm_errormessage: message.slice(0, 2000),
      dagrun_pm_issuetype: 100000000,
      dagrun_pm_resolutionstatus: 100000001,
      'dagrun_pm_importjobid@odata.bind': `/dagrun_pm_importjobs(${jobId})`,
    });
  } catch { /* logging is best-effort — never block the import */ }
}

/**
 * Bulk-create records in batches of IMPORT_BATCH_SIZE. Returns the created
 * GUIDs index-aligned to the input (null where a row failed) so callers can
 * resolve cross-references (e.g. predecessor dependencies). When jobId is set,
 * failed rows are logged to dagrun_pm_importlog and dagrun_pm_rowsimported /
 * dagrun_pm_rowsfailed are PATCHed incrementally after each batch.
 */
export async function batchInsert(
  pca: IPublicClientApplication,
  entitySet: string,
  bodies: Record<string, unknown>[],
  opts: { jobId?: string; rowIndexOffset?: number; onProgress?: (p: BatchProgress) => void } = {},
): Promise<{ ids: (string | null)[]; imported: number; failed: number }> {
  const total = bodies.length;
  const batchCount = Math.ceil(total / IMPORT_BATCH_SIZE);
  const ids: (string | null)[] = [];
  const offset = opts.rowIndexOffset ?? 0;
  let imported = 0;
  let failed = 0;

  for (let b = 0; b < batchCount; b++) {
    const chunk = bodies.slice(b * IMPORT_BATCH_SIZE, (b + 1) * IMPORT_BATCH_SIZE);
    const { ids: chunkIds, errors } = await dataverseBatchCreate(pca, entitySet, chunk);
    for (let i = 0; i < chunk.length; i++) {
      ids.push(chunkIds[i]);
      if (chunkIds[i] && !errors[i]) {
        imported++;
      } else {
        failed++;
        if (opts.jobId) await createImportLogEntry(pca, opts.jobId, offset + b * IMPORT_BATCH_SIZE + i + 1, errors[i] ?? 'Unknown error');
      }
    }
    if (opts.jobId) {
      try { await updateImportJob(pca, opts.jobId, { dagrun_pm_rowsimported: imported, dagrun_pm_rowsfailed: failed }); } catch { /* best-effort */ }
    }
    opts.onProgress?.({ batchIndex: b + 1, batchCount, imported, failed, total });
  }

  return { ids, imported, failed };
}
